import React, {useEffect, useState} from "react";
import {Box, List, Stack, Typography} from "@mui/material";
import {IconAlertTriangle, IconGripVertical, IconTrash} from "@tabler/icons-react";
import {NodeType, NodeTypeIcon} from "../../../../../model/ModelData";
import useConfirm from "../../../../../hooks/useConfirm";
import {sortFormNodes} from "../formNodesSort";
import {EditMenuHeader, HeaderIconButton} from "./EditMenuHeader";
import {EditMenuRow} from "./EditMenuRow";
import {CreateFormNodeButton} from "./CreateFormNodeButton";
import {UI, rgba} from "../../uiTokens";

/**
 * The form panel: an ordered, drag-reorderable list of the nodes that make up a
 * form, an "unconnected nodes" bucket, and the add-node button.
 */
export const FormNodesPanel = ({node, value, setValue, items, onRemove, onClose, addNode, onUpdateNodes}) => {
    const [sortedItems, setSortedItems] = useState([]);
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);

    useEffect(() => {
        if (items && items.length) {
            const startNode = items.find(i => i.data?.form_id === node.data.form_id && i.type === NodeType.START_FORM);
            if (startNode) {
                setSortedItems(sortFormNodes(startNode, items));
            }
        }
        setDraggedItem(null);
    }, [items]);

    const confirmDelete = useConfirm("Are you sure you want to delete this node?", node?.name || "");

    const onDelete = async () => {
        const response = await confirmDelete();
        if (response && node?.data?.form_id) {
            onRemove(node?.data?.form_id);
            onClose();
        }
    };

    const onDragEnd = async () => {
        if (draggedItem?.id === dragOverItem?.id) {
            setDraggedItem(null);
            setDragOverItem(null);
            return;
        }
        let newItems = [...items];
        const newDraggedItem = newItems.find(n => n.id === draggedItem.id);
        const prevItem = newItems.find(n => n?.routes?.static?.next?.target === draggedItem.id);
        const nextItem = newItems.find(n => n?.id === draggedItem?.routes?.static?.next?.target);
        if (prevItem && newDraggedItem) {
            prevItem.routes.static.next.target = nextItem.id || null;
        }
        if (dragOverItem === null) {
            newDraggedItem.routes.static.next.target = null;
            await onUpdateNodes(newItems);
            setDraggedItem(null);
            setDragOverItem(null);
            return;
        }
        const newDragOverItem = newItems.find(n => n.id === dragOverItem.id);
        const dragOverNextItem = newItems.find(n => n?.id === newDragOverItem?.routes?.static?.next?.target);

        if (newDragOverItem && dragOverNextItem) {
            newDragOverItem.routes.static.next.target = newDraggedItem.id || null;
            newDraggedItem.routes.static.next.target = dragOverNextItem.id || null;
        }
        await onUpdateNodes(newItems);

        setDraggedItem(null);
        setDragOverItem(null);
    };

    const dragHandle = (nodeItem) => (
        <Box
            draggable
            onDragStart={() => setDraggedItem(nodeItem)}
            onDragEnd={onDragEnd}
            sx={{display: "flex", cursor: "grab", color: UI.muted}}
        >
            <IconGripVertical size={14}/>
        </Box>
    );

    const unconnected = items?.filter(nodeItem => sortedItems?.findIndex(i => i.id === nodeItem?.id) === -1) || [];

    return (
        <Stack
            direction="column"
            gap={1.5}
            sx={{
                p: 1,
                flex: 1,
                borderRight: `1px solid ${value ? rgba(UI.accent, 0.14) : "transparent"}`,
            }}
        >
            <EditMenuHeader title="Form" onBack={onClose}>
                <HeaderIconButton label="Delete form" danger onClick={onDelete}>
                    <IconTrash size={17}/>
                </HeaderIconButton>
            </EditMenuHeader>

            <List sx={{my: 0, py: 0}}>
                {sortedItems?.map((nodeItem, index) => {
                    const Icon = NodeTypeIcon[nodeItem?.type] || IconAlertTriangle;
                    const isNotDroppable = index !== 0 && index !== sortedItems?.length - 1;
                    return (
                        <EditMenuRow
                            key={nodeItem?.id}
                            className={dragOverItem?.id === nodeItem?.id ? "shake" : ""}
                            icon={<Icon size={18}/>}
                            primary={nodeItem?.name}
                            secondary={nodeItem?.type?.replaceAll("_", " ")}
                            capitalizeSecondary
                            active={value?.id === nodeItem?.id}
                            onClick={() => setValue(value?.id === nodeItem?.id ? null : nodeItem)}
                            onDragOver={() => setDragOverItem(nodeItem)}
                            trailing={isNotDroppable ? dragHandle(nodeItem) : null}
                        />
                    );
                })}
            </List>

            {unconnected.length > 0 && (
                <Box sx={{p: 1, borderRadius: "10px", backgroundColor: rgba(UI.accent, 0.06)}}>
                    <Typography sx={{fontSize: 11.5, fontWeight: 700, color: UI.ink, mb: 0.5}}>
                        Nodes that exist in the form but are not connected
                    </Typography>
                    <List sx={{p: 0}}>
                        {unconnected.map((nodeItem) => {
                            const Icon = NodeTypeIcon[nodeItem?.type] || IconAlertTriangle;
                            return (
                                <EditMenuRow
                                    key={nodeItem?.id}
                                    icon={<Icon size={18}/>}
                                    primary={nodeItem?.name}
                                    secondary={nodeItem?.type?.replaceAll("_", " ")}
                                    capitalizeSecondary
                                    active={value?.id === nodeItem?.id}
                                    onClick={() => setValue(value?.id === nodeItem?.id ? null : nodeItem)}
                                    trailing={dragHandle(nodeItem)}
                                />
                            );
                        })}
                    </List>
                </Box>
            )}

            <CreateFormNodeButton onSelect={addNode}/>
        </Stack>
    );
};
